import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, Warning, CaretRight } from "@phosphor-icons/react";
import { api, money, shortDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StageBadge, EmptyState } from "@/components/common";

export default function ReviewQueue() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);

  useEffect(() => {
    api.get("/contracts/queue/for-me").then((r) => setRows(r.data)).catch(() => {});
  }, []);

  const subtitle = {
    grant_manager: "Contracts awaiting your funding & grant-alignment review.",
    contract_specialist: "Documentation & compliance review. Records carry a 30-day review window.",
    legal_reviewer: "Final internal checkpoint before execution.",
  }[user?.role] || "Records awaiting review.";

  return (
    <div>
      <PageHeader overline={`${user?.role_label} · Queue`} title="Review Queue">{subtitle}</PageHeader>

      {rows.length === 0 ? (
        <EmptyState title="Your queue is clear" subtitle="No records are currently awaiting your review." />
      ) : (
        <div className="space-y-3">
          {rows.map((c) => {
            const dr = c.days_remaining;
            const overdue = c.is_overdue;
            return (
              <button
                key={c.id}
                data-testid={`queue-row-${c.id}`}
                onClick={() => navigate(`/contracts/${c.id}`)}
                className={`w-full flex items-center justify-between bg-card border rounded-md p-5 text-left transition-colors hover:border-primary ${overdue ? "border-alert/50" : "border-border"}`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-head font-semibold text-foreground">{c.title}</span>
                    <StageBadge stage={c.workflow_stage} />
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">{c.contract_number} · {c.grantee_vendor} · {money(c.amount)}</div>
                </div>
                <div className="flex items-center gap-5 shrink-0">
                  <div className={`text-right ${overdue ? "text-alert" : "text-muted-foreground"}`}>
                    <div className="flex items-center gap-1.5 justify-end text-sm font-medium">
                      {overdue ? <Warning size={15} /> : <Clock size={15} />}
                      {overdue ? `${Math.abs(dr)}d overdue` : `${dr}d left`}
                    </div>
                    <div className="text-[11px]">Due {shortDate(c.review?.due_at)}</div>
                  </div>
                  <CaretRight size={18} className="text-muted-foreground" />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
